import { Check, Plus, Tag } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/envelope';
import { useDismissOnOutside } from '../../lib/useDismiss';

/** Set equality, order-insensitive — the server answers with the set, not an order. */
const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const held = (set: string[], title: string) =>
  set.some((one) => one.toLowerCase() === title.toLowerCase());

/**
 * Put labels on one conversation, in whichever layout is drawing it.
 *
 * The endpoint REPLACES, so every toggle sends the whole set — which is also
 * what makes two agents labelling one thread at the same moment agree on an
 * answer instead of each applying half of one. A title nobody has used before
 * is created by that same write, which is why there is no "new label" screen
 * anywhere and why the host re-reads the catalog afterwards rather than this
 * component guessing what the server made of it.
 *
 * Controlled apart from one optimistic copy. `applied` is the row's own set and
 * the row is re-read after the write, so between the server's ack and that
 * re-read the prop still holds the OLD set — a tick painted from it reads as
 * the agent's own click being undone. [wanted] covers that gap, and is dropped
 * only once the row agrees or the write comes back refused.
 */
export function LabelPicker({
  applied,
  catalog,
  disabled = false,
  disabledTitle,
  className = 'cbtn cbtn-icon',
  iconSize = 14,
  align = 'right',
  onApply,
  onError,
}: {
  /** What this conversation carries now, off the row. */
  applied: string[];
  /** Every label the tenant has — the list under the box. */
  catalog: string[];
  disabled?: boolean;
  /** Why it is disabled, in the star button's words so the two agree. */
  disabledTitle?: string;
  /** The host's button shape: bordered in the thread header, round in the mail toolbar. */
  className?: string;
  iconSize?: number;
  /**
   * Which edge the menu hangs from. Right suits a button at the end of a
   * header; a toolbar button near the left edge needs the other one, or a
   * 230px menu opens off the side of the pane.
   */
  align?: 'left' | 'right';
  /**
   * Writes the WHOLE set. Must REJECT when the server refused, or the
   * optimistic copy outlives the refusal it was wrong about.
   */
  onApply: (next: string[]) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [wanted, setWanted] = useState<string[] | null>(null);
  const menuRef = useDismissOnOutside<HTMLDivElement>(open, () => setOpen(false));
  const inputRef = useRef<HTMLInputElement>(null);

  const current = wanted ?? applied;

  useEffect(() => {
    // The row caught up with what was asked for. Stop overriding it — otherwise
    // a colleague's later change to the same thread is masked by ours.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (wanted && sameSet(wanted, applied)) setWanted(null);
  }, [wanted, applied]);

  /**
   * The catalog, plus whatever this conversation carries that is not in it. The
   * union matters on the one read that fails quietly: `listLabels` is tolerated
   * empty (a config-only admin gets 1036 from it), and without the union the
   * menu would offer to ADD a label the thread already has.
   */
  const options = useMemo(() => {
    const seen = new Map<string, string>();
    for (const title of [...catalog, ...applied]) {
      const key = title.toLowerCase();
      if (!seen.has(key)) seen.set(key, title);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [catalog, applied]);

  const typed = query.trim();
  const shown = typed
    ? options.filter((title) => title.toLowerCase().includes(typed.toLowerCase()))
    : options;
  const creatable = typed !== '' && !held(options, typed);

  const apply = async (next: string[]) => {
    setWanted(next);
    try {
      await onApply(next);
    } catch (e) {
      setWanted(null);
      onError(e instanceof ApiError ? e.message : 'That did not go through.');
    }
  };

  const toggle = (title: string) => {
    void apply(
      held(current, title)
        ? current.filter((one) => one.toLowerCase() !== title.toLowerCase())
        : [...current, title],
    );
  };

  const create = () => {
    if (!creatable) return;
    setQuery('');
    inputRef.current?.focus();
    void apply([...current, typed]);
  };

  return (
    <div className="thr-head-menu">
      <button
        type="button"
        className={`lbl-btn ${className}${current.length ? ' has-labels' : ''}`}
        disabled={disabled}
        title={
          disabled
            ? (disabledTitle ?? 'You have read-only access to this channel')
            : current.length
              ? `Labels: ${current.join(', ')}`
              : 'Label this conversation'
        }
        onClick={() => {
          setOpen((was) => !was);
          setQuery('');
        }}
      >
        <Tag size={iconSize} />
      </button>
      {open && (
        <div className={`thr-menu lbl-menu${align === 'left' ? ' lbl-menu-left' : ''}`} ref={menuRef}>
          <input
            ref={inputRef}
            className="lbl-find"
            type="text"
            autoFocus
            aria-label="Find or create a label"
            placeholder="Find or create a label"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                return;
              }
              if (e.key !== 'Enter') return;
              // Enter is whichever of the two the typing has left unambiguous:
              // a title nobody holds yet, or the one row still standing.
              if (creatable) create();
              else if (shown.length === 1) toggle(shown[0]);
            }}
          />
          <div className="lbl-list">
            {shown.map((title) => (
              <button key={title} type="button" className="lbl-row" onClick={() => toggle(title)}>
                {held(current, title) ? <Check size={13} /> : <span className="lbl-tick" />}
                <span className="lbl-title">{title}</span>
              </button>
            ))}
            {!shown.length && !creatable && (
              <div className="thr-menu-note">
                {options.length ? 'No label by that name.' : 'No labels yet — type one to create it.'}
              </div>
            )}
          </div>
          {creatable && (
            <button type="button" className="lbl-row lbl-create" onClick={create}>
              <Plus size={13} />
              <span className="lbl-title">Create &ldquo;{typed}&rdquo;</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
